import { z } from 'zod'
import * as http from '@/util/http'

export const AddressSchema = z.object({
  id: z.number().int().positive().optional(),

  name: z.string().nullable().optional(), // nullable=True

  postalCode: z.string().min(1),

  subnationalUnit: z.string().min(1),
  adminDivision: z.string().min(1),

  city: z
    .string()
    .nonempty({ message: '市区町村を入力してください。' })
    .max(50, { message: '市区町村は50文字以内で入力してください。' })
    .refine((s) => /^[^\d]+$/.test(s), { message: '市区町村に数字は含められません。' }),

  district: z
    .string()
    .max(50, { message: '町域・字は50文字以内で入力してください。' })
    .refine((s) => /^[^\d]*$/.test(s), { message: '町域・字に数字は含められません。' })
    .optional(),

  addressLine1: z.string().nonempty({ message: '丁目・番地を入力してください。' }),
  addressLine2: z.string().nullable().optional(),

  phoneNumber: z.string().nullable().optional(),
})

export type AddressForm = z.infer<typeof AddressSchema>

export const DEFAULT_ADDRESS: AddressForm = {
  postalCode: '',
  subnationalUnit: '',
  adminDivision: '',
  city: '',
  district: '',
  addressLine1: '',
  addressLine2: '',
}

export async function fetchUserFirstAddr() {
  const { data: addrs } = await http.get<AddressForm[]>('/api/v1/user/addr')
  return !addrs || !addrs.length ? DEFAULT_ADDRESS : addrs[0]
}

export const ZipCloudResultSchema = z.object({
  address1: z.string(), // 都道府縣
  address2: z.string(), // 市区町村
  address3: z.string(), // 町域
  kana1: z.string(), // 都道府縣（カナ）
  kana2: z.string(), // 市区町村（カナ）
  kana3: z.string(), // 町域（カナ）
  prefcode: z.string(), // 都道府縣代碼
  zipcode: z.string(), // 郵便番号
})

export const ZipCloudResponseSchema = z.object({
  message: z.string().nullable(),
  results: z.array(ZipCloudResultSchema).nullable(),
  status: z.number(),
})

export type ZipCloudResponse = z.infer<typeof ZipCloudResponseSchema>

// TODO: find a clean way to do it
export const PREFECTURE_TO_ENUM: Record<string, string> = {
  北海道: 'Hokkaido',
  青森県: 'Aomori',
  岩手県: 'Iwate',
  宮城県: 'Miyagi',
  秋田県: 'Akita',
  山形県: 'Yamagata',
  福島県: 'Fukushima',
  茨城県: 'Ibaraki',
  栃木県: 'Tochigi',
  群馬県: 'Gunma',
  埼玉県: 'Saitama',
  千葉県: 'Chiba',
  東京都: 'Tokyo',
  神奈川県: 'Kanagawa',
  新潟県: 'Niigata',
  富山県: 'Toyama',
  石川県: 'Ishikawa',
  福井県: 'Fukui',
  山梨県: 'Yamanashi',
  長野県: 'Nagano',
  岐阜県: 'Gifu',
  静岡県: 'Shizuoka',
  愛知県: 'Aichi',
  三重県: 'Mie',
  滋賀県: 'Shiga',
  京都府: 'Kyoto',
  大阪府: 'Osaka',
  兵庫県: 'Hyogo',
  奈良県: 'Nara',
  和歌山県: 'Wakayama',
  鳥取県: 'Tottori',
  島根県: 'Shimane',
  岡山県: 'Okayama',
  広島県: 'Hiroshima',
  山口県: 'Yamaguchi',
  徳島県: 'Tokushima',
  香川県: 'Kagawa',
  愛媛県: 'Ehime',
  高知県: 'Kochi',
  福岡県: 'Fukuoka',
  佐賀県: 'Saga',
  長崎県: 'Nagasaki',
  熊本県: 'Kumamoto',
  大分県: 'Oita',
  宮崎県: 'Miyazaki',
  鹿児島県: 'Kagoshima',
  沖縄県: 'Okinawa',
}
